// Live-tracking links for a competitor.
//
// An event's races each carry the prefix of their tracking address (configured on
// the Event dialog — see components/EventRaceManager.jsx); a competitor's page is
// that prefix with their race number appended, e.g.
//   https://live.opentracking.co.uk/26Dragon30/?b=  +  236
// Each race is a separate event on the tracking provider, hence one prefix per
// race rather than one per event.

// Race names are matched loosely, because both sides are typed by people: the
// event's configured name against the incident's patient_race, which was copied
// from the imported entry list.
const key = (s) => (s || "").trim().toLowerCase();

// Only http(s) is ever opened. The backend validates the prefix on the way in
// (schemas/event.py), so this is the second of two checks — a "javascript:" value
// would otherwise run in the reader's own session.
const isSafe = (url) => /^https?:\/\//i.test(url || "");

/**
 * The tracking URL for one competitor, or null when there isn't one.
 *
 * `races` is the event's configured list, `race` the incident's race name and
 * `raceNumber` the competitor's number. A race name that matches nothing gives
 * null rather than a guess — except that an event with exactly one configured
 * race is used for an incident with no race recorded at all, which is the common
 * case for a patient typed in by hand rather than picked off the entry list.
 */
export function trackingUrl(races, race, raceNumber) {
  const number = (raceNumber || "").trim();
  if (!number || !(races || []).length) return null;

  const named = races.find((r) => key(r.name) === key(race));
  const match = named || (!key(race) && races.length === 1 ? races[0] : null);
  if (!match || !isSafe(match.tracking_url_prefix)) return null;
  return match.tracking_url_prefix + encodeURIComponent(number);
}

/** Open a tracking URL in a new tab, with no handle back to this window. */
export function openTracking(url) {
  if (isSafe(url)) window.open(url, "_blank", "noopener,noreferrer");
}
